// The time zone picker's options, from the zones api/timezones lists. Offsets and current times come from the
// browser's own time zone data, so they follow daylight saving.

const zoneFormats = new Map();

// One formatter per zone (null when the browser doesn't know the zone); it gives the offset and the browser's name.
function zoneFormat(id) {
	if (!zoneFormats.has(id)) {
		let format = null;
		try {
			format = new Intl.DateTimeFormat("en-US", { timeZone: id, timeZoneName: "longOffset" });
		} catch (e) {
			format = null;
		}
		zoneFormats.set(id, format);
	}
	return zoneFormats.get(id);
}

/** The zone's UTC offset in minutes at the given time, or null when the browser doesn't know the zone. */
export function offsetMinutes(id, date) {
	const format = zoneFormat(id);
	if (format === null) {
		return null;
	}
	// "GMT-03:00", "GMT+05:45", or "GMT" for UTC itself
	const text = format.formatToParts(date).find((part) => part.type === "timeZoneName").value.replace("GMT", "");
	if (text === "") {
		return 0;
	}
	const sign = text.startsWith("-") ? -1 : 1;
	const [hours, minutes] = text.slice(1).split(":");
	return sign * (Number(hours) * 60 + Number(minutes || 0));
}

/** "UTC-3", "UTC+5:30", "UTC+0"; empty when the offset is unknown. */
export function formatOffset(minutes) {
	if (minutes === null) {
		return "";
	}
	const absolute = Math.abs(minutes);
	const remainder = absolute % 60;
	const hours = `${minutes < 0 ? "-" : "+"}${Math.floor(absolute / 60)}`;
	return `UTC${hours}${remainder === 0 ? "" : `:${String(remainder).padStart(2, "0")}`}`;
}

// The spellings of an offset people type: UTC-3, GMT-3, -03:00, UTC-03:00.
function offsetSearchText(minutes) {
	if (minutes === null) {
		return "";
	}
	const absolute = Math.abs(minutes);
	const sign = minutes < 0 ? "-" : "+";
	const hhmm = `${sign}${String(Math.floor(absolute / 60)).padStart(2, "0")}:${String(absolute % 60).padStart(2, "0")}`;
	const short = formatOffset(minutes);
	return `${short} ${short.replace("UTC", "GMT")} ${hhmm} UTC${hhmm}`;
}

const timeFormats = new Map();

/** The current time in the zone, in the page's language ("17:33", "5:33 PM"); empty when the browser can't tell. */
export function timeIn(id, language, date) {
	const key = `${language}|${id}`;
	if (!timeFormats.has(key)) {
		let format = null;
		try {
			format = new Intl.DateTimeFormat(language, { timeZone: id, hour: "numeric", minute: "2-digit" });
		} catch (e) {
			format = null;
		}
		timeFormats.set(key, format);
	}
	const format = timeFormats.get(key);
	return format === null ? "" : format.format(date);
}

/**
 * The listed zone that is this browser's own time zone, or null. Some browsers use a zone's old name
 * (Asia/Calcutta for Asia/Kolkata), so a zone also matches when the browser names it that way.
 */
export function browserZoneId(entries) {
	let local = null;
	try {
		local = Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch (e) {
		return null;
	}
	if (!local) {
		return null;
	}
	const exact = entries.find((entry) => entry.id === local);
	if (exact) {
		return exact.id;
	}
	const renamed = entries.find((entry) => {
		const format = zoneFormat(entry.id);
		return format !== null && format.resolvedOptions().timeZone === local;
	});
	return renamed ? renamed.id : null;
}

/**
 * The pieces a chosen zone shows in the closed field: its title, then its current UTC offset, country and time zone
 * name, leaving out a piece that is empty or repeats the title. Joined by " · ", they name the zone in an alert's time
 * filter, on the page and on the server (TimezoneCatalog.ClosedText).
 */
export function closedPieces(option) {
	const details = [formatOffset(option.offset), option.country, option.name]
		.filter((detail) => detail !== "" && detail !== option.title);
	return [option.title, ...details];
}

function toOption(entry, date) {
	const offset = offsetMinutes(entry.id, date);
	const isFixed = !entry.country && Boolean(entry.name);
	let title = entry.city || entry.id;
	if (isFixed) {
		title = offset === 0 ? "UTC" : formatOffset(offset);
	}
	return {
		id: entry.id,
		title,
		country: entry.country || "",
		name: entry.name || "",
		offset,
		search: `${entry.id} ${entry.id.split("/").join(" ").split("_").join(" ")} ${offsetSearchText(offset)}`,
	};
}

/**
 * Tom Select options for the listed zones, ordered by current offset and then by place. A stored zone the list lacks
 * is offered as itself, so the field shows the zone it holds. The browser's own zone is also in the "local" group.
 */
export function timezoneOptions(entries, storedId, localId, language, date) {
	const options = entries.map((entry) => toOption(entry, date));
	if (storedId && !options.some((option) => option.id === storedId)) {
		options.push(toOption({ id: storedId }, date));
	}
	let collator = null;
	try {
		collator = new Intl.Collator(language);
	} catch (e) {
		collator = new Intl.Collator();
	}
	const unknownLast = (offset) => (offset === null ? Number.MAX_SAFE_INTEGER : offset);
	options.sort((a, b) => unknownLast(a.offset) - unknownLast(b.offset) || collator.compare(a.title, b.title));
	options.forEach((option, index) => {
		option.order = index;
		option.group = option.id === localId ? ["local", "all"] : "all";
	});
	return options;
}
